import React, { useState, useCallback } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { deleteProjectById } from '@Services/Projects';
import { deleteProject } from '@Services/Projects/Request';

import useNetwork from '@Hooks/useNetwork';
import useContextMenu from '@Hooks/useContextMenu';

import MoreSVG from '@Assets/Svgs/ic_more.svg';
import TX from '@Components/Common/Text';
import { ContextMenu, Item } from '@Components/Common/ContextMenu';

import dayjs from 'dayjs';

import localizedFormat from 'dayjs/plugin/localizedFormat'; // 로컬라이즈된 포맷 플러그인을 가져옵니다.
import updateLocale from 'dayjs/plugin/updateLocale'; // 로케일 업데이트 플러그인을 가져옵니다.
import NoticeDeleteProjectModal from './NoticeDeleteProjectModal';

import 'dayjs/locale/ko';

dayjs.locale('ko');

dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

// 오후/오전을 '오후', '오전'으로 표기하도록 로케일 업데이트
dayjs.updateLocale('ko', {
  meridiem: hour => {
    return hour < 12 ? '오전' : '오후';
  },
});

const PUBLISH_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
};

// const PUBLISH_STATUS_KOR = {
//   [PUBLISH_STATUS.DRAFT]: '출시 안 함',
//   [PUBLISH_STATUS.PUBLISHED]: '출시함',
// };

const ProjectItem = ({ projectId, name, publishStatus, showing, projectSuspension, createdAtTimeStamp }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNetworkConnected = useNetwork();

  const [isNoticeDeleteProjectModalVisible, setIsNoticeDeleteProjectModalVisible] = useState(false);
  const { isContextMenuVisible, handleShowContextMenuClick } = useContextMenu();

  const handleIsNoticeDeleteProjectModalVisible = useCallback(() => {
    setIsNoticeDeleteProjectModalVisible(!isNoticeDeleteProjectModalVisible);
  }, [isNoticeDeleteProjectModalVisible]);

  const dispatchProjectDelete = useCallback(() => {
    dispatch(deleteProjectById({ projectId }));
  }, [dispatch]);

  const handleEditorMoveClick = useCallback(() => {
    if (isNetworkConnected) {
      navigate({
        pathname: '/editor',
        search: `${createSearchParams({
          id: `${projectId}`,
        }).toString()}`,
      });
    }
  }, [isNetworkConnected, projectId]);

  const handleProjectSettingMoveClick = () =>
    navigate({
      pathname: '/project_setting',
      search: `${createSearchParams({
        id: `${projectId}`,
      }).toString()}`,
    });

  const handleProjectDeleteClick = async () => {
    await Promise.all([deleteProject(projectId), dispatchProjectDelete()]);
  };

  // UNIX 타임스탬프를 변환
  const latestUpdateTime = dayjs.unix(createdAtTimeStamp).locale('ko').format('YYYY년 MM월 DD일 A h시 mm분');

  const renderPublishInfo = () => {
    const style = {
      backgroundColor: 'rgba(201, 193, 244, 0.6)', // 기본 배경색
      color: 'var(--Primary, #674af6)', // 기본 텍스트 색
    };
    let text = '';

    if (projectSuspension) {
      style.backgroundColor = 'rgba(255, 85, 85, 0.10)';
      style.color = '#FF5555';
      text = '차단됨';
    } else if (publishStatus === PUBLISH_STATUS.PUBLISHED) {
      if (showing) {
        text = '출시';
      } else {
        style.backgroundColor = 'rgba(128, 128, 128, 0.13)';
        style.color = '#808080';
        text = '비공개 출시';
      }
    }

    if (!text) {
      return null;
    }

    return (
      <PublishInfo style={{ backgroundColor: style.backgroundColor }}>
        <PublishInfoText style={{ color: style.color }}>{text}</PublishInfoText>
      </PublishInfo>
    );
  };

  return (
    <>
      <Container>
        <ProjectInfoContainer isClickable={isNetworkConnected} onClick={handleEditorMoveClick}>
          <TitleText>{name}</TitleText>
          {/* <LaunchText>{PUBLISH_STATUS_KOR[publishStatus]}</LaunchText> */}
          <LaunchText>마지막 수정 {latestUpdateTime}</LaunchText>
        </ProjectInfoContainer>
        {renderPublishInfo()}
        <MoreButton onClick={event => handleShowContextMenuClick(event)}>
          <MoreImage />

          {isContextMenuVisible && (
            <ContextMenu position={{ right: 0, top: 36.5 }}>
              <Item onClick={handleProjectSettingMoveClick}>
                <ContextMenuItemText>프로젝트 설정</ContextMenuItemText>
              </Item>

              <Item onClick={handleIsNoticeDeleteProjectModalVisible}>
                <ContextMenuItemText isDelete>삭제</ContextMenuItemText>
              </Item>
            </ContextMenu>
          )}
        </MoreButton>
      </Container>

      <NoticeDeleteProjectModal
        isNoticeDeleteProjectModalVisible={isNoticeDeleteProjectModalVisible}
        handleIsNoticeDeleteProjectModalVisible={handleIsNoticeDeleteProjectModalVisible}
        handleProjectDeleteClick={handleProjectDeleteClick}
      />
    </>
  );
};

ProjectItem.propTypes = {
  projectId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  publishStatus: PropTypes.string.isRequired,
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 69px;

  padding: 12px;
  margin-bottom: 8px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.ui_2};
`;

const ProjectInfoContainer = styled.div`
  flex: 1;
  height: 100%;
  justify-content: space-between;

  cursor: ${({ isClickable }) => isClickable && 'pointer'};
`;

const PublishInfo = styled.div`
  display: flex;
  padding: 2px 4px;
  align-items: flex-start;
  gap: 10px;
  border-radius: 3px;
  background: rgba(201, 193, 244, 0.6);
`;

const PublishInfoText = styled.text`
  color: var(--Primary, #674af6);
  font-feature-settings: 'clig' off, 'liga' off;

  /* FootnoteM */
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 123.077% */
  letter-spacing: -0.3px;
`;

const TitleText = styled(TX.BodyB)`
  color: ${({ theme }) => theme.colors.ui_on_1};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.21px;

  display: block;
`;

const LaunchText = styled(TX.FootnoteM)`
  color: ${({ theme }) => theme.colors.ui_on_3};
  letter-spacing: -0.3px;
`;

const MoreButton = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
`;

const MoreImage = styled.img.attrs({ src: MoreSVG, alt: '더보기' })`
  width: 16px;
  height: 16px;
`;

const ContextMenuItemText = styled(TX.FootnoteM)`
  color: ${({ isDelete, theme }) => (isDelete ? theme.colors.danger : theme.colors.ui_on_1)};
`;

export default ProjectItem;

// import React, { useState, useCallback } from 'react';
// import { createSearchParams, useNavigate } from 'react-router-dom';
// import { useDispatch } from 'react-redux';
// import styled from 'styled-components';
// import PropTypes from 'prop-types';

// import { deleteProjectById } from '@Services/Projects';
// import { deleteProject } from '@Services/Projects/Request';

// import useNetwork from '@Hooks/useNetwork';
// import useContextMenu from '@Hooks/useContextMenu';

// import MoreSVG from '@Assets/Svgs/ic_more.svg';
// import TX from '@Components/Common/Text';
// import { ContextMenu, Item } from '@Components/Common/ContextMenu';

// import NoticeDeleteProjectModal from './NoticeDeleteProjectModal';

// const PUBLISH_STATUS = {
//   DRAFT: 'draft',
//   PUBLISHED: 'published',
// };

// const PUBLISH_STATUS_KOR = {
//   [PUBLISH_STATUS.DRAFT]: '출시 안 함',
//   [PUBLISH_STATUS.PUBLISHED]: '출시함',
// };

// const ProjectItem = ({ projectId, name, publishStatus }) => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const isNetworkConnected = useNetwork();

//   const [isNoticeDeleteProjectModalVisible, setIsNoticeDeleteProjectModalVisible] = useState(false);
//   const { isContextMenuVisible, handleShowContextMenuClick } = useContextMenu();

//   const handleIsNoticeDeleteProjectModalVisible = useCallback(() => {
//     setIsNoticeDeleteProjectModalVisible(!isNoticeDeleteProjectModalVisible);
//   }, [isNoticeDeleteProjectModalVisible]);

//   const dispatchProjectDelete = useCallback(() => {
//     dispatch(deleteProjectById({ projectId }));
//   }, [dispatch]);

//   const handleEditorMoveClick = useCallback(() => {
//     if (isNetworkConnected) {
//       navigate({
//         pathname: '/editor',
//         search: `${createSearchParams({
//           id: `${projectId}`,
//         }).toString()}`,
//       });
//     }
//   }, [isNetworkConnected, projectId]);

//   const handleProjectSettingMoveClick = () =>
//     navigate({
//       pathname: '/project_setting',
//       search: `${createSearchParams({
//         id: `${projectId}`,
//       }).toString()}`,
//     });

//   const handleProjectDeleteClick = async () => {
//     await Promise.all([deleteProject(projectId), dispatchProjectDelete()]);
//   };

//   return (
//     <>
//       <Container>
//         <ProjectInfoContainer isClickable={isNetworkConnected} onClick={handleEditorMoveClick}>
//           <TitleText>{name}</TitleText>
//           <LaunchText>{PUBLISH_STATUS_KOR[publishStatus]}</LaunchText>
//         </ProjectInfoContainer>

//         <MoreButton onClick={event => handleShowContextMenuClick(event)}>
//           <MoreImage />

//           {isContextMenuVisible && (
//             <ContextMenu position={{ right: 0, top: 36.5 }}>
//               <Item onClick={handleProjectSettingMoveClick}>
//                 <ContextMenuItemText>프로젝트 설정</ContextMenuItemText>
//               </Item>

//               <Item onClick={handleIsNoticeDeleteProjectModalVisible}>
//                 <ContextMenuItemText isDelete>삭제</ContextMenuItemText>
//               </Item>
//             </ContextMenu>
//           )}
//         </MoreButton>
//       </Container>

//       <NoticeDeleteProjectModal
//         isNoticeDeleteProjectModalVisible={isNoticeDeleteProjectModalVisible}
//         handleIsNoticeDeleteProjectModalVisible={handleIsNoticeDeleteProjectModalVisible}
//         handleProjectDeleteClick={handleProjectDeleteClick}
//       />
//     </>
//   );
// };

// ProjectItem.propTypes = {
//   projectId: PropTypes.string.isRequired,
//   name: PropTypes.string.isRequired,
//   publishStatus: PropTypes.string.isRequired,
// };

// const Container = styled.div`
//   display: flex;
//   flex-direction: row;
//   align-items: center;
//   justify-content: center;

//   width: 100%;
//   height: 69px;

//   padding: 12px;
//   margin-bottom: 8px;
//   border-radius: 12px;
//   background-color: ${({ theme }) => theme.colors.ui_2};
// `;

// const ProjectInfoContainer = styled.div`
//   flex: 1;
//   height: 100%;
//   justify-content: space-between;

//   cursor: ${({ isClickable }) => isClickable && 'pointer'};
// `;

// const TitleText = styled(TX.BodyB)`
//   color: ${({ theme }) => theme.colors.ui_on_1};
//   font-size: 14px;
//   line-height: 22px;
//   letter-spacing: -0.21px;

//   display: block;
// `;

// const LaunchText = styled(TX.FootnoteM)`
//   color: ${({ theme }) => theme.colors.ui_on_3};
//   letter-spacing: -0.3px;
// `;

// const MoreButton = styled.div`
//   display: flex;
//   width: 32px;
//   height: 32px;
//   align-items: center;
//   justify-content: center;
//   position: relative;
//   cursor: pointer;
// `;

// const MoreImage = styled.img.attrs({ src: MoreSVG, alt: '더보기' })`
//   width: 16px;
//   height: 16px;
// `;

// const ContextMenuItemText = styled(TX.FootnoteM)`
//   color: ${({ isDelete, theme }) => (isDelete ? theme.colors.danger : theme.colors.ui_on_1)};
// `;

// export default ProjectItem;
